import { 
  TARGET_RANGES, 
  CHEMICAL_DATA, 
  STATUS_LEVELS, 
  DOSING_PREFERENCES, 
  CONVERSIONS,
  calculateAcidDosing,
  CRITICAL_THRESHOLD_MULTIPLIER
} from './constants'

/**
 * Calculate critical thresholds for a parameter range
 * @param {Object} range - Target range with min/max values
 * @param {number} multiplier - Multiplier for critical threshold calculation (default 0.5)
 * @returns {Object} Critical low and high thresholds
 */
export const calculateCriticalThresholds = (range, multiplier = 0.5) => {
  const { min, max } = range
  return {
    criticalLow: min - (max - min) * multiplier,
    criticalHigh: max + (max - min) * multiplier
  }
}

/**
 * Calculate target ranges for all parameters based on current conditions
 * @param {Object} poolConfig - Pool configuration (volume, type)
 * @param {Object} currentMeasurement - Current water chemistry readings
 * @returns {Object} Target ranges for each parameter
 */
export const calculateTargetRanges = (poolConfig, currentMeasurement) => {
  if (!poolConfig?.volume_gallons || poolConfig.volume_gallons <= 0) {
    throw new Error('Pool volume must be greater than zero')
  }

  const { pool_type } = poolConfig
  const { cya_stabilizer } = currentMeasurement
  
  // Calculate CYA-dependent free chlorine targets
  // Use ?? instead of || so that CYA=0 is treated as a real value (no stabilizer)
  const cyaLevel = cya_stabilizer ?? TARGET_RANGES.CYA_STABILIZER.ideal
  const fcMin = Math.max(
    cyaLevel * TARGET_RANGES.FREE_CHLORINE.min_multiplier,
    TARGET_RANGES.FREE_CHLORINE.absolute_min
  )
  const fcTarget = Math.min(
    cyaLevel * TARGET_RANGES.FREE_CHLORINE.target_multiplier,
    TARGET_RANGES.FREE_CHLORINE.absolute_max
  )
  const fcMax = Math.min(
    cyaLevel * TARGET_RANGES.FREE_CHLORINE.target_multiplier * 1.1, // 10% above target as max
    TARGET_RANGES.FREE_CHLORINE.absolute_max
  )
  
  // Get calcium hardness range based on pool type
  const chRange = TARGET_RANGES.CALCIUM_HARDNESS[pool_type] || 
                  TARGET_RANGES.CALCIUM_HARDNESS.vinyl_liner
  
  return {
    free_chlorine: {
      min: fcMin,
      max: fcMax,
      ideal: fcTarget
    },
    combined_chlorine: TARGET_RANGES.COMBINED_CHLORINE,
    ph: TARGET_RANGES.PH,
    total_alkalinity: TARGET_RANGES.TOTAL_ALKALINITY,
    cya_stabilizer: TARGET_RANGES.CYA_STABILIZER,
    calcium_hardness: chRange,
    water_temperature: TARGET_RANGES.WATER_TEMPERATURE
  }
}

/**
 * Determine status level for a parameter value
 * @param {number} value - Current parameter value
 * @param {Object} range - Target range for the parameter
 * @returns {string} Status level constant
 */
export const getParameterStatus = (value, range) => {
  if (value === null || value === undefined) {
    return STATUS_LEVELS.UNKNOWN
  }
  
  const { min, max } = range
  const { criticalLow, criticalHigh } = calculateCriticalThresholds(range, CRITICAL_THRESHOLD_MULTIPLIER)
  
  if (value < criticalLow) return STATUS_LEVELS.CRITICAL_LOW
  if (value > criticalHigh) return STATUS_LEVELS.CRITICAL_HIGH
  if (value < min) return STATUS_LEVELS.LOW
  if (value > max) return STATUS_LEVELS.HIGH
  return STATUS_LEVELS.GOOD
}

/**
 * Calculate chemical recommendations based on current readings
 * @param {Object} poolConfig - Pool configuration
 * @param {Object} currentMeasurement - Current readings
 * @param {Object} targetRanges - Target ranges for parameters
 * @returns {Array} Array of chemical recommendations
 */
export const calculateRecommendations = (poolConfig, currentMeasurement, targetRanges) => {
  if (!poolConfig?.volume_gallons || poolConfig.volume_gallons <= 0) {
    return []
  }

  const recommendations = []
  const poolVolumeRatio = poolConfig.volume_gallons / CONVERSIONS.gallons_per_10k
  
  // Total Chlorine recommendations (Free + Combined)
  const fcCurrent = Math.max(0, currentMeasurement.free_chlorine || 0)
  const ccCurrent = Math.max(0, currentMeasurement.combined_chlorine || 0)
  const tcCurrent = fcCurrent + ccCurrent
  const fcTarget = targetRanges.free_chlorine.ideal
  const tcDifference = fcTarget - tcCurrent
  
  if (Math.abs(tcDifference) > 0.5) { // Only recommend if difference > 0.5 ppm
    if (tcDifference > 0) {
      const ppmToAdd = Math.min(tcDifference, DOSING_PREFERENCES.max_single_dose.free_chlorine)
      
      // Primary recommendation: Liquid chlorine (12.5%) using TFP calculation
      const liquidChlorine = CHEMICAL_DATA.liquid_chlorine_12_5
      // TFP Formula: tfp_constant = pool gallons that 1 oz raises by 1 PPM
      // Ounces needed = (ppm to add × pool volume) / tfp_constant
      const ouncesNeeded = (ppmToAdd * poolConfig.volume_gallons) / liquidChlorine.tfp_constant
      
      // TODO: Use last chemical type used by user (for now default to liquid chlorine)
      recommendations.push({
        parameter: 'free_chlorine',
        issue: `Total Chlorine: ${tcCurrent.toFixed(1)}ppm, Target: ${fcTarget.toFixed(1)}ppm`,
        chemical: 'liquid_chlorine_12_5',
        chemicalName: liquidChlorine.name,
        amount: ouncesNeeded,
        unit: 'fluid_ounce',
        priority: getParameterStatus(tcCurrent, targetRanges.free_chlorine) === STATUS_LEVELS.CRITICAL_LOW ? 'high' : 'medium',
        expectedEffect: `Add ${formatAmount(ouncesNeeded, 'fluid_ounce')} to raise by ${ppmToAdd.toFixed(1)}ppm`
      })
    }
  }
  
  // pH recommendations
  const phCurrent = currentMeasurement.ph
  if (phCurrent !== null && phCurrent !== undefined) {
    const phTarget = targetRanges.ph.ideal
    const phDifference = phTarget - phCurrent
    
    if (Math.abs(phDifference) > 0.1) {
      if (phDifference > 0) {
        // Need to raise pH
        const chemical = CHEMICAL_DATA.soda_ash
        const phRaise = Math.min(Math.abs(phDifference), DOSING_PREFERENCES.max_single_dose.ph_increase)
        const amountNeeded = phRaise / chemical.ph_increase_per_lb_per_10k_gal * poolVolumeRatio
        
        recommendations.push({
          parameter: 'ph',
          issue: `pH: ${phCurrent.toFixed(1)}, Target: ${phTarget.toFixed(1)}`,
          chemical: 'soda_ash',
          chemicalName: chemical.name,
          amount: amountNeeded,
          unit: 'pound',
          priority: Math.abs(phDifference) > 0.3 ? 'high' : 'medium',
          expectedEffect: `Add ${formatAmount(amountNeeded, 'pound')} to raise by ${phRaise.toFixed(1)}`,
          warning: phRaise < Math.abs(phDifference) ? 'Large pH adjustment - this is the first dose. Test and repeat if needed.' : null
        })
      } else {
        // Need to lower pH - use lookup table approach
        const phLower = Math.min(Math.abs(phDifference), DOSING_PREFERENCES.max_single_dose.ph_decrease)
        const targetPH = phCurrent - phLower
        const currentTA = currentMeasurement.total_alkalinity || 100
        
        // Calculate using lookup table for 12.5% acid
        const ouncesFor10k = calculateAcidDosing(phCurrent, targetPH, currentTA)
        const ouncesNeeded = ouncesFor10k * poolVolumeRatio
        
        // TODO: Use last acid type used by user (for now default to 12.5%)
        const acid12 = CHEMICAL_DATA.muriatic_acid_12
        recommendations.push({
          parameter: 'ph',
          issue: `pH: ${phCurrent.toFixed(1)}, Target: ${phTarget.toFixed(1)}`,
          chemical: 'muriatic_acid_12',
          chemicalName: acid12.name,
          amount: ouncesNeeded,
          unit: 'fluid_ounce',
          priority: Math.abs(phDifference) > 0.3 ? 'high' : 'medium',
          expectedEffect: `Add ${formatAmount(ouncesNeeded, 'fluid_ounce')} to lower by ${phLower.toFixed(1)}`,
          warning: 'Add acid slowly to deep end with pump running. Never add acid to chlorine.'
        })
      }
    }
  }
  
  // Total Alkalinity recommendations
  const taCurrent = currentMeasurement.total_alkalinity
  if (taCurrent !== null && taCurrent !== undefined) {
    const taTarget = targetRanges.total_alkalinity.ideal
    const taDifference = taTarget - taCurrent
    
    if (Math.abs(taDifference) > 10) {
      if (taDifference > 0) {
        // Need to raise TA
        const chemical = CHEMICAL_DATA.baking_soda
        const taRaise = Math.min(taDifference, DOSING_PREFERENCES.max_single_dose.total_alkalinity)
        const amountNeeded = taRaise / chemical.ta_increase_per_lb_per_10k_gal * poolVolumeRatio
        
        recommendations.push({
          parameter: 'total_alkalinity',
          issue: `Total Alkalinity: ${taCurrent}ppm, Target: ${taTarget}ppm`,
          chemical: 'baking_soda',
          chemicalName: chemical.name,
          amount: amountNeeded,
          unit: 'pound',
          priority: Math.abs(taDifference) > 40 ? 'high' : 'medium',
          expectedEffect: `Add ${formatAmount(amountNeeded, 'pound')} to raise by ${taRaise}ppm`
        })
      }
    }
  }
  
  // CYA recommendations
  const cyaCurrent = currentMeasurement.cya_stabilizer || 0
  const cyaTarget = targetRanges.cya_stabilizer.ideal
  const cyaDifference = cyaTarget - cyaCurrent
  
  if (cyaDifference > 5) { // Only recommend adding CYA, never removing
    const chemical = CHEMICAL_DATA.dry_stabilizer
    const cyaRaise = Math.min(cyaDifference, DOSING_PREFERENCES.max_single_dose.cya_stabilizer)
    const amountNeeded = cyaRaise / chemical.cya_increase_per_lb_per_10k_gal * poolVolumeRatio
    
    recommendations.push({
      parameter: 'cya_stabilizer',
      issue: `CYA: ${cyaCurrent}ppm, Target: ${cyaTarget}ppm`,
      chemical: 'dry_stabilizer',
      chemicalName: chemical.name,
      amount: amountNeeded,
      unit: 'pound',
      priority: cyaCurrent < 20 ? 'high' : 'low',
      expectedEffect: `Add ${formatAmount(amountNeeded, 'pound')} to raise by ${cyaRaise}ppm`,
      warning: 'CYA dissolves slowly. Use sock method and takes 24-48 hours to fully dissolve.'
    })
  }
  
  // Calcium Hardness recommendations
  const chCurrent = currentMeasurement.calcium_hardness
  if (chCurrent !== null && chCurrent !== undefined) {
    const chTarget = targetRanges.calcium_hardness.ideal
    const chDifference = chTarget - chCurrent
    
    if (chDifference > 20) { // Only recommend adding CH, never removing
      const chemical = CHEMICAL_DATA.calcium_chloride
      const chRaise = Math.min(chDifference, DOSING_PREFERENCES.max_single_dose.calcium_hardness)
      const amountNeeded = chRaise / chemical.calcium_increase_per_lb_per_10k_gal * poolVolumeRatio
      
      recommendations.push({
        parameter: 'calcium_hardness',
        issue: `Calcium Hardness: ${chCurrent}ppm, Target: ${chTarget}ppm`,
        chemical: 'calcium_chloride',
        chemicalName: chemical.name,
        amount: amountNeeded,
        unit: 'pound',
        priority: chCurrent < 100 ? 'high' : 'low',
        expectedEffect: `Add ${formatAmount(amountNeeded, 'pound')} to raise by ${chRaise}ppm`
      })
    }
  }
  
  // Sort recommendations by priority
  const priorityOrder = { high: 3, medium: 2, low: 1 }
  recommendations.sort((a, b) => priorityOrder[b.priority] - priorityOrder[a.priority])
  
  return recommendations
}

/**
 * Get user-friendly conversion for amounts
 * @param {number} amount - Raw amount
 * @param {string} unit - Unit of measurement
 * @returns {string} User-friendly conversion
 */
export const getFriendlyConversion = (amount, unit) => {
  if (unit === 'ounce' || unit === 'fluid_ounce') {
    const cups = amount / 8
    const gallons = amount / 128
    
    if (cups >= 7) {
      // Convert to gallons if over 7 cups
      if (gallons >= 1) {
        return `≈ ${gallons.toFixed(1)} ${gallons === 1 ? 'gallon' : 'gallons'}`
      } else {
        const quarts = amount / 32
        return `≈ ${quarts.toFixed(1)} ${quarts === 1 ? 'quart' : 'quarts'}`
      }
    } else if (cups >= 1) {
      return `≈ ${cups.toFixed(1)} ${cups === 1 ? 'cup' : 'cups'}`
    } else {
      const tablespoons = amount * 2
      if (tablespoons >= 1) {
        return `≈ ${tablespoons.toFixed(0)} tbsp`
      }
      return null // Too small for meaningful conversion
    }
  } else if (unit === 'gallon') {
    const quarts = amount * 4
    const cups = amount * 16
    
    if (amount < 0.25) {
      return `≈ ${cups.toFixed(1)} ${cups === 1 ? 'cup' : 'cups'}`
    } else if (amount < 1) {
      return `≈ ${quarts.toFixed(1)} ${quarts === 1 ? 'quart' : 'quarts'}`
    }
    return null // Already in gallons
  } else if (unit === 'pound') {
    const ounces = amount * 16
    const cups = ounces / 5 // Approximate for dry ingredients
    
    if (amount < 1 && cups >= 1) {
      return `≈ ${cups.toFixed(1)} ${cups === 1 ? 'cup' : 'cups'}`
    }
    return null
  }
  return null
}

/**
 * Format amount for display
 * @param {number} amount - Raw amount
 * @param {string} unit - Unit of measurement
 * @returns {string} Formatted amount string
 */
export const formatAmount = (amount, unit) => {
  if (unit === 'gallon') {
    if (amount >= 1) {
      return `${amount.toFixed(1)} ${amount === 1 ? 'gallon' : 'gallons'}`
    } else {
      const quarts = amount * 4
      if (quarts >= 1) {
        return `${quarts.toFixed(1)} ${quarts === 1 ? 'quart' : 'quarts'}`
      } else {
        const ounces = amount * 128
        return `${ounces.toFixed(0)} fl oz`
      }
    }
  } else if (unit === 'pound') {
    if (amount >= 1) {
      return `${amount.toFixed(1)} ${amount === 1 ? 'lb' : 'lbs'}`
    } else {
      const ounces = amount * 16
      return `${ounces.toFixed(1)} oz`
    }
  } else if (unit === 'ounce') {
    if (amount >= 32) {
      const quarts = amount / 32
      return `${quarts.toFixed(1)} ${quarts === 1 ? 'quart' : 'quarts'}`
    } else if (amount >= 16) {
      const pints = amount / 16
      return `${pints.toFixed(1)} ${pints === 1 ? 'pint' : 'pints'}`
    } else {
      return `${amount.toFixed(1)} fl oz`
    }
  } else if (unit === 'fluid_ounce') {
    if (amount >= 128) {
      const gallons = amount / 128
      return `${gallons.toFixed(2)} ${gallons === 1 ? 'gallon' : 'gallons'}`
    } else if (amount >= 32) {
      const quarts = amount / 32
      return `${quarts.toFixed(1)} ${quarts === 1 ? 'quart' : 'quarts'}`
    } else if (amount >= 16) {
      const pints = amount / 16
      return `${pints.toFixed(1)} ${pints === 1 ? 'pint' : 'pints'}`
    } else if (amount >= 8) {
      const cups = amount / 8
      return `${cups.toFixed(1)} ${cups === 1 ? 'cup' : 'cups'}`
    } else {
      return `${amount.toFixed(1)} fl oz`
    }
  }
  return `${amount.toFixed(2)} ${unit}`
}

/**
 * Format amount with user-friendly conversion for display
 * @param {number} amount - Raw amount
 * @param {string} unit - Unit of measurement
 * @returns {Object} {primary, friendly} formatted strings
 */
export const formatAmountWithConversion = (amount, unit) => {
  const primary = formatAmount(amount, unit)
  const friendly = getFriendlyConversion(amount, unit)
  
  return {
    primary,
    friendly,
    tooltip: friendly ? `${primary} (${friendly})` : primary
  }
}

// Status configuration for consistent handling
const STATUS_CONFIG = {
  [STATUS_LEVELS.CRITICAL_LOW]: { color: 'error', text: 'Critical Low' },
  [STATUS_LEVELS.CRITICAL_HIGH]: { color: 'error', text: 'Critical High' },
  [STATUS_LEVELS.LOW]: { color: 'warning', text: 'Low' },
  [STATUS_LEVELS.HIGH]: { color: 'warning', text: 'High' },
  [STATUS_LEVELS.GOOD]: { color: 'success', text: 'Good' },
  [STATUS_LEVELS.UNKNOWN]: { color: 'info', text: 'Unknown' }
}

/**
 * Get status color for Material UI
 * @param {string} status - Status level constant
 * @returns {string} Material UI color name
 */
export const getStatusColor = (status) => {
  return STATUS_CONFIG[status]?.color || 'info'
}

/**
 * Get status text for display
 * @param {string} status - Status level constant
 * @returns {string} Human readable status
 */
export const getStatusText = (status) => {
  return STATUS_CONFIG[status]?.text || 'Unknown'
}

/**
 * Calculate the impact of adding a specific chemical
 * @param {string} chemicalKey - Key from CHEMICAL_DATA
 * @param {number} amount - Amount to add
 * @param {Object} poolConfig - Pool configuration
 * @param {Object} currentMeasurement - Current water chemistry
 * @returns {Object} Impact description and affected parameters
 */
export const calculateChemicalImpact = (chemicalKey, amount, poolConfig, currentMeasurement) => {
  const chemical = CHEMICAL_DATA[chemicalKey]
  if (!chemical) {
    return { description: 'Unknown chemical', effects: {} }
  }

  if (!poolConfig?.volume_gallons || poolConfig.volume_gallons <= 0) {
    throw new Error('Pool volume must be greater than zero')
  }

  // Clamp amount to non-negative
  const safeAmount = Math.max(0, amount || 0)

  const poolVolumeRatio = poolConfig.volume_gallons / CONVERSIONS.gallons_per_10k
  const effects = {}
  let description = ''

  // Convert amount to ounces based on chemical unit
  const toOunces = (amt, unit) => {
    if (unit === 'fluid_ounce') return amt
    if (unit === 'gallon') return amt * 128   // fl oz per gallon
    if (unit === 'pound') return amt * 16     // weight oz per pound
    return amt
  }

  // Calculate effects based on chemical properties
  if (chemical.affects.includes('free_chlorine')) {
    let fcIncrease = 0
    if (chemical.tfp_constant) {
      // TFP methodology: tfp_constant = pool gallons where 1 oz raises FC by 1 PPM
      // FC increase (ppm) = (ounces × tfp_constant) / pool_volume
      const ounces = toOunces(safeAmount, chemical.unit)
      fcIncrease = (ounces * chemical.tfp_constant) / poolConfig.volume_gallons
    } else if (chemical.unit === 'gallon' && chemical.density) {
      // Fallback: Legacy liquid chlorine calculation
      const poundsOfSolution = safeAmount * chemical.density
      const poundsOfActiveChlorine = poundsOfSolution * (chemical.concentration / 100)
      fcIncrease = poundsOfActiveChlorine * 1000000 / (poolConfig.volume_gallons * 8.34)
    } else if (chemical.concentration) {
      // Fallback: Legacy powder/granular calculation
      const poundsOfActiveChlorine = safeAmount * (chemical.concentration / 100)
      fcIncrease = poundsOfActiveChlorine * 1000000 / (poolConfig.volume_gallons * 8.34)
    } else {
      // Fallback calculation - this shouldn't happen with properly configured chemicals
      console.warn(`Chemical ${chemicalKey} missing TFP constant, using fallback calculation`)
      fcIncrease = 0
    }
    
    const currentFC = currentMeasurement.free_chlorine || 0
    const newFC = currentFC + fcIncrease
    effects.free_chlorine = { from: currentFC, to: newFC, change: fcIncrease }
    description = `Raise Free Chlorine from ${currentFC.toFixed(1)}ppm to ${newFC.toFixed(1)}ppm`
  }

  // Define parameter configurations for consistent handling
  const parameterConfigs = [
    {
      parameter: 'ph',
      properties: ['ph_increase_per_lb_per_10k_gal', 'ph_decrease_per_gal_per_10k_gal'],
      defaultValue: 7.4,
      formatDecimals: 1,
      unit: '',
      label: 'pH'
    },
    {
      parameter: 'total_alkalinity', 
      properties: ['ta_increase_per_lb_per_10k_gal', 'ta_decrease_per_gal_per_10k_gal'],
      defaultValue: 100,
      formatDecimals: 0,
      unit: 'ppm',
      label: 'TA'
    },
    {
      parameter: 'cya_stabilizer',
      properties: ['cya_increase_per_lb_per_10k_gal', 'cya_increase_per_gal_per_10k_gal', 'cya_increase'],
      defaultValue: 0,
      formatDecimals: 0,
      unit: 'ppm',
      label: 'CYA'
    },
    {
      parameter: 'calcium_hardness',
      properties: ['calcium_increase_per_lb_per_10k_gal'],
      defaultValue: 200,
      formatDecimals: 0,
      unit: 'ppm',
      label: 'CH'
    }
  ]

  // Process each parameter that the chemical affects
  parameterConfigs.forEach(config => {
    if (chemical.affects.includes(config.parameter)) {
      let change = 0
      
      // Calculate change based on available properties
      for (const property of config.properties) {
        if (chemical[property]) {
          if ((property === 'cya_increase' && (chemicalKey === 'trichlor' || chemicalKey === 'dichlor')) ||
              (property === 'calcium_increase' && (chemicalKey === 'cal_hypo_65' || chemicalKey === 'cal_hypo_73'))) {
            // Special case for side effects that are per 10 ppm FC added
            const ounces = toOunces(safeAmount, chemical.unit)
            const fcAdded = (ounces * chemical.tfp_constant) / poolConfig.volume_gallons
            change = (fcAdded / 10) * chemical[property]
          } else {
            change = safeAmount * chemical[property] * poolVolumeRatio
          }
          break
        }
      }
      
      const current = currentMeasurement[config.parameter] || config.defaultValue
      const newValue = current + change
      effects[config.parameter] = { from: current, to: newValue, change }
      
      if (description) description += '; '
      const direction = change > 0 ? 'Raise' : 'Lower'
      description += `${direction} ${config.label} from ${current.toFixed(config.formatDecimals)}${config.unit} to ${newValue.toFixed(config.formatDecimals)}${config.unit}`
    }
  })

  return {
    description: description || 'Chemical addition calculated',
    effects
  }
}

/**
 * Generate tooltip explanation for parameter status
 * @param {string} parameterKey - Parameter key (e.g., 'free_chlorine', 'ph')
 * @param {string} status - Status level
 * @param {Object} range - Target range for the parameter
 * @param {number} value - Current parameter value
 * @param {Object} poolConfig - Pool configuration
 * @param {Object} currentMeasurement - Current measurement readings
 * @returns {string} Tooltip explanation text
 */
export const getStatusTooltip = (parameterKey, status, range, value, poolConfig, currentMeasurement) => {
  const formatRange = (min, max, unit = '') => {
    return `${min}${unit} - ${max}${unit}`
  }

  let rangeExplanation = ''

  // Base range information
  const unit = parameterKey === 'ph' ? '' : ' ppm'
  const targetRange = formatRange(range.min, range.max, unit)

  switch (parameterKey) {
    case 'free_chlorine':
      const cyaLevel = currentMeasurement?.cya_stabilizer || 40
      rangeExplanation = `Free Chlorine target is CYA-dependent (7.5-12% of CYA level). With CYA at ${cyaLevel} ppm, target range is ${targetRange}.`
      break
    
    case 'ph':
      rangeExplanation = `pH target range is ${targetRange} for optimal chlorine effectiveness and swimmer comfort.`
      break
    
    case 'total_alkalinity':
      rangeExplanation = `Total Alkalinity target range is ${targetRange} for liquid chlorine users to maintain pH stability.`
      break
    
    case 'cya_stabilizer':
      rangeExplanation = `CYA target range is ${targetRange} to protect chlorine from UV degradation without over-stabilization.`
      break
    
    case 'calcium_hardness':
      const poolType = poolConfig?.pool_type || 'vinyl_liner'
      const poolTypeText = poolType.replace('_', ' ')
      rangeExplanation = `Calcium Hardness target for ${poolTypeText} pools is ${targetRange} to prevent scaling or corrosion.`
      break
    
    default:
      rangeExplanation = `Target range is ${targetRange}.`
  }

  // Add critical threshold information for critical statuses
  if (status === STATUS_LEVELS.CRITICAL_LOW || status === STATUS_LEVELS.CRITICAL_HIGH) {
    const { criticalLow, criticalHigh } = calculateCriticalThresholds(range, CRITICAL_THRESHOLD_MULTIPLIER)
    
    const criticalInfo = status === STATUS_LEVELS.CRITICAL_LOW 
      ? ` Critical below ${criticalLow.toFixed(unit === '' ? 1 : 0)}${unit}.`
      : ` Critical above ${criticalHigh.toFixed(unit === '' ? 1 : 0)}${unit}.`
    
    return rangeExplanation + criticalInfo
  }

  // Return just the range explanation for non-critical statuses
  return rangeExplanation
}